<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useFormat } from '../composables/useFormat'
import type { SensitivityPoint } from '../model/fit'

/**
 * Cost per pod against how wrong the requests are, −90% to +90%. Each column is a
 * button: picking one updates the readout, and the choice is shared by both
 * comparison slots so they answer the same question.
 */
const props = defineProps<{
  points: readonly SensitivityPoint[]
  step: number
  /** Hours in the chosen pricing interval. */
  hours: number
}>()

const emit = defineEmits<{ 'update:step': [number] }>()

const { t } = useI18n()
const format = useFormat()

// Columns grow from zero: a truncated axis would exaggerate small differences.
const max = computed(() =>
  Math.max(0, ...props.points.map((p) => p.costPerPod).filter((c) => Number.isFinite(c))),
)

function height(point: SensitivityPoint): string {
  if (!Number.isFinite(point.costPerPod)) return '100%'
  return max.value > 0 ? `${Math.max(2, (point.costPerPod / max.value) * 100)}%` : '2%'
}

const percent = (step: number): string => format.percent(Math.abs(step) / 10)

function label(point: SensitivityPoint): string {
  const cost = format.price(point.costPerPod * props.hours)
  if (point.step === 0) return t('cost.sensitivity.exact', { cost })
  const key = point.step < 0 ? 'cost.sensitivity.lower' : 'cost.sensitivity.higher'
  return t(key, { percent: percent(point.step), cost })
}
</script>

<template>
  <div class="sensitivity">
    <div class="sensitivity__plot" role="group" :aria-label="t('cost.sensitivity.heading')">
      <button
        v-for="point in points"
        :key="point.step"
        type="button"
        class="sensitivity__column"
        :class="{
          'sensitivity__column--selected': point.step === step,
          'sensitivity__column--none': !Number.isFinite(point.costPerPod),
        }"
        :aria-pressed="point.step === step"
        :aria-label="label(point)"
        :title="label(point)"
        @click="emit('update:step', point.step)"
      >
        <span class="sensitivity__bar" :style="{ height: height(point) }" />
      </button>
    </div>
    <div class="sensitivity__axis" aria-hidden="true">
      <span>−{{ percent(9) }}</span>
      <span>{{ t('cost.sensitivity.axis') }}</span>
      <span>+{{ percent(9) }}</span>
    </div>
  </div>
</template>

<style scoped>
.sensitivity {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}

.sensitivity__plot {
  display: flex;
  align-items: stretch;
  gap: 2px;
  height: 5rem;
  border-bottom: 1px solid var(--color-border);
}

.sensitivity__column {
  display: flex;
  flex: 1;
  align-items: flex-end;
  min-width: 0;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: pointer;
}

.sensitivity__bar {
  width: 100%;
  border-radius: 4px 4px 0 0;
  background: var(--color-chart-1);
  opacity: 0.35;
  transition: opacity var(--transition-fast);
}

.sensitivity__column:hover .sensitivity__bar {
  opacity: 0.6;
}

.sensitivity__column--selected .sensitivity__bar {
  opacity: 1;
}

/* Nothing fits at this size: a full-height column, hatched, rather than a price. */
.sensitivity__column--none .sensitivity__bar {
  background: repeating-linear-gradient(
    135deg,
    var(--color-border-strong) 0 3px,
    transparent 3px 6px
  );
  opacity: 1;
}

.sensitivity__axis {
  display: flex;
  justify-content: space-between;
  font-size: var(--text-sm);
  color: var(--color-text-muted);
}
</style>
